import { memo, type FC, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export type AccordionSectionProps = {
  /** Section title displayed in the header */
  title: string;
  /** Optional ID for the accordion section (for E2E testing) */
  id?: string;
  /** Whether the section is expanded */
  expanded: boolean;
  /** Callback when header is clicked (optional - omit for auto-controlled sections) */
  onToggle?: () => void;
  /** Badge count to display next to title */
  badge?: number;
  /** Optional adornment rendered next to the title and badge */
  titleAdornment?: ReactNode;
  /** Action element (e.g., Clear button) displayed in header */
  action?: ReactNode;
  /** Optional override for content Box styles */
  contentSx?: Record<string, unknown>;
  /** Content to render when expanded */
  children: ReactNode;
};

/**
 * Collapsible accordion section following FilterPanel's visual language.
 * Can be auto-controlled (no onToggle) or manually controlled.
 */
export const AccordionSection: FC<AccordionSectionProps> = memo(
  ({
    title,
    id,
    expanded,
    onToggle,
    badge,
    titleAdornment,
    action,
    contentSx,
    children,
  }) => {
    const isClickable = !!onToggle;

    return (
      <Box id={id}>
        <Box
          onClick={onToggle}
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: isClickable ? "pointer" : "default",
            bgcolor: "grey.50",
            borderBottom: 1,
            borderColor: "divider",
            "&:hover": isClickable ? { bgcolor: "grey.100" } : undefined,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: "text.secondary",
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s",
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              {title}
            </Typography>
            {badge !== undefined && badge > 0 && (
              <Chip
                id={id ? `${id}-badge` : undefined}
                label={badge}
                size="small"
                sx={{
                  height: 20,
                  minWidth: 20,
                  fontSize: "0.75rem",
                  bgcolor: "primary.lighter",
                  color: "primary.dark",
                }}
              />
            )}
            {titleAdornment && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {titleAdornment}
              </Box>
            )}
          </Box>

          {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: 2, py: 1.5, ...contentSx }}>{children}</Box>
        </Collapse>
      </Box>
    );
  }
);

AccordionSection.displayName = "AccordionSection";
